import { A } from '@solidjs/router';
import { For, type JSX, Show } from 'solid-js';
import type { AuditFeedEvent } from '../lib/audit-feed';
import { orgHref } from '../lib/org';
import { absolute, clockOrDay } from '../lib/time';

// Account audit feed rows (#854). Presenter — same feed-row visuals as the Home
// "Live audit" panel (`home-audit` block): a primary dot, the tool (or event
// kind) as a title linking into the session's full timeline, a mono repo · kind
// meta line, and a right-aligned wall-clock stamp. Repo-less gateway rows show
// the session id where the repo would be.
//
// #1222: the title falls through `tool ?? verb ?? kind`. `integration.used`
// carries no `tool` (domain invariant — it is not a tool-call kind), so without
// the `verb` rung every row in the connections usage panel reads the same
// literal `integration.used`. Both fields are null on every other kind, so the
// extra rung and the outcome suffix are no-ops outside that one feed.

export function AuditFeedList(props: { events: readonly AuditFeedEvent[] }): JSX.Element {
  return (
    <ul class="home-audit">
      <For each={props.events}>
        {(e) => (
          <li class="home-audit__row">
            <span class="home-audit__dot" aria-hidden="true" />
            <div class="home-audit__lead">
              {/* Only a repo-scoped row has a session detail to open. The detail
                  endpoint is fenced through the repo join on purpose —
                  `findSessionDetailForAccount`: "repo-less gateway parents stay
                  invisible to both" (it and the SSE stream) — so linking one is a
                  guaranteed 404, which is what every account-tail row did (#1395).
                  They still belong in the feed: the account tail includes them
                  deliberately (#823). They are just not doors. */}
              <Show
                when={e.repo_full_name}
                fallback={<span class="home-audit__title">{e.tool ?? e.verb ?? e.kind}</span>}
              >
                <A class="home-audit__title" href={orgHref(`/audit/${e.session_id}`)}>
                  {e.tool ?? e.verb ?? e.kind}
                </A>
              </Show>
              <p class="home-audit__meta">
                {e.repo_full_name ?? e.session_id}
                {' · '}
                {e.kind}
                <Show when={e.outcome}>{(o) => <> · {o()}</>}</Show>
              </p>
            </div>
            <time
              class="home-audit__time"
              datetime={e.created_at_iso}
              title={absolute(e.created_at_iso)}
            >
              {clockOrDay(e.created_at_iso)}
            </time>
          </li>
        )}
      </For>
    </ul>
  );
}
